import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { cn } from '@/lib/utils';
import type { Contact } from '@/shared/types/contacts';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { ContactActionsCell } from '../components/ContactActionsCell';
import { getContactAvatarColor, getInitials, sourceFlavour, sourceLabelKey } from '../constants';

export const contactColumns = (): ColumnDef<Contact>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
    cell: ({ row }) => {
      const { name, contact_id } = row.original;
      return (
        <div className="flex items-center gap-3">
          <Avatar className="size-8">
            <AvatarFallback
              className={cn('text-xs text-primary-foreground', getContactAvatarColor(contact_id))}
            >
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 truncate font-medium">{name || t('contacts.unnamed')}</div>
          <RowActionIndicator action="view" />
        </div>
      );
    },
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX', className: 'pl-6' },
  },
  {
    id: 'identity',
    accessorFn: (row) => row.email || row.phone || '',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('contacts.identity')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { email, phone } = row.original;
      const value = email || phone;
      if (!value) {
        return <span className="text-muted-foreground">—</span>;
      }
      return <span className="block truncate text-sm text-foreground">{value}</span>;
    },
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: ({ column }) => <SortableHeader column={column}>{t('contacts.source')}</SortableHeader>,
    cell: ({ row }) => {
      const source = row.original.source;
      if (!source) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <Badge type="secondary" flavour={sourceFlavour[source]} rounded={false}>
          {t(sourceLabelKey[source])}
        </Badge>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'updated',
    accessorKey: 'updated',
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_updated')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { updated } = row.original;
      if (!updated) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <TimestampTooltip timestamp={updated}>
          <span className="text-sm text-foreground">{timeFromNow(updated)}</span>
        </TimestampTooltip>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'actions',
    header: () => '',
    cell: ({ row }) => <ContactActionsCell contact={row.original} />,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
];
